import logger from 'helpers/logger';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, {
    forwardRef,
    memo,
    Suspense,
    useImperativeHandle,
    useRef,
    useCallback,
    useEffect
} from 'react';
import { LoadingIndicator } from 'sharedComponents';
import { apiCallStatus } from 'helpers/constants';

const LazyEChartComponent = React.lazy(async () => {
    const { default: ReactECharts } = await import(/* webpackChunkName: "echarts-for-react" */ 'echarts-for-react');
    const echarts = await import(/* webpackChunkName: "echarts" */ 'echarts');
    await import(/* webpackChunkName: "echarts-gl" */ 'echarts-gl');

    // ECharts doesn't require AST configuration like Highcharts
    // Custom symbol drawing and plugin imports are not applicable to ECharts

    const InternalChart = (props, ref) => {
        const containerRef = useRef(null);
        const chartRef = useRef(null);
        const creatingChartRef = useRef(false);

        const createUpdateChart = useCallback(debounce((debProps) => {
            if (!chartRef.current && !creatingChartRef.current) {
                creatingChartRef.current = true;
            } else {
                if (chartRef?.current) {
                    logger.debug('Update echart', debProps.container);
                    if (debProps.setChartLoader) {
                        debProps.setChartLoader(apiCallStatus.SUCCESS);
                    }
                }
                else {
                    logger.warn('Update skipped, invalid echart instance!');
                }
            }
        }, 100), []);

        const handleChartReady = useCallback((chartInstance) => {
            if (chartInstance && !chartRef.current) {
                chartRef.current = chartInstance;
                creatingChartRef.current = false;
                logger.debug('Chart ready', props.container);

                // Call the callback if provided
                if (props.callback) {
                    props.callback(chartInstance);
                }
            }
        }, [props.callback, props.container]);

        useEffect(() => {
            return () => {
                createUpdateChart.cancel(); // cancel delayed execution
                // Destroy chart only if unmounting.
                if (chartRef.current) {
                    if (props.destroy) {
                        props.destroy(null);
                    }
                    chartRef.current = null;
                    logger.debug('Chart destroyed', props.container);
                }
            };
        }, []);


        useEffect(() => {
            createUpdateChart(props);
        }, [props.options, props.options2D, createUpdateChart]);

        // Explicitly handle 2D chart updates
        useEffect(() => {
            if (props.chart2DRef?.current && props.options2D) {
                const chart2DInstance = props.chart2DRef.current?.getEchartsInstance
                    ? props.chart2DRef.current.getEchartsInstance()
                    : props.chart2DRef.current;
                if (chart2DInstance && typeof chart2DInstance.setOption === 'function') {
                    setTimeout(() => {
                        chart2DInstance.setOption(props.options2D, true);
                    }, 0);
                }
            }
        }, [props.options2D]);

        useImperativeHandle(
            ref,
            () => ({
                get chart() {
                    return chartRef.current;
                },
                getEchartsInstance: () => chartRef.current,
                container: containerRef
            }),
            []
        );

        return (props?.options?.series?.length > 0 || props?.options?.graphic) &&
            <>
                <ReactECharts
                    ref={containerRef}
                    echarts={echarts}
                    option={props.options}
                    style={{ minHeight: '600px', width: '100%' }}
                    notMerge={true}
                    lazyUpdate={true}
                    onChartReady={handleChartReady}
                />
                {/* Render 2D chart for zoom navigator */}
                {props.options2D &&
                    <ReactECharts
                        ref={props.chart2DRef}
                        option={props.options2D}
                        onEvents={props.on2DEvents}
                        style={{ height: 45, width: '100%' }}
                    />
                }
            </>;
    };
    InternalChart.displayName = 'InternalEChart3D';

    InternalChart.propTypes = {
        modules: PropTypes.arrayOf(PropTypes.string),
        options: PropTypes.object,
        callback: PropTypes.func,
        destroy: PropTypes.func,
        container: PropTypes.string,
        setChartLoader: PropTypes.func
    };

    return {
        default: memo(forwardRef(InternalChart))
    };
});

const EChart3D = forwardRef((props, ref) => {
    return <Suspense fallback={<LoadingIndicator />}>
        <LazyEChartComponent {...props} ref={ref} />
    </Suspense>;
});

EChart3D.displayName = 'LazyEChartWrapper3D';

export default EChart3D;
